<script lang="ts">
    import { onMount } from "svelte";
    import { LL } from "../../../i18n/i18n-svelte";
    import { gameManager } from "../../Phaser/Game/GameManager";
    import {
        pendingBuildingFootprintStore,
        type BuildingFootprint,
    } from "../../Stores/BuildingEditorStore";

    // The Track-2 materials feed: every catalog family with its per-world
    // enabled state. Namespaced ids ("roof:slate-mauve"); mansard roof families
    // are the "roof:shingle-*" ones, banded roof families are the rest.
    interface MaterialFamily {
        id: string;
        name: string;
        kind: "roof" | "wall" | "storefront";
        enabled: boolean;
    }

    let families = $state<MaterialFamily[]>([]);
    let loadError = $state<string | null>(null);
    let status = $state<string | null>(null);
    let submitting = $state(false);

    let kind = $state<"banded" | "mansard">("banded");
    let roofFamily = $state<string>("");
    let storefront = $state<string>("");
    let floorCount = $state(1);
    let floorFamilies = $state<string[]>([""]);

    const footprint = $derived<BuildingFootprint | null>($pendingBuildingFootprintStore);

    function roomToken(): string | undefined {
        return gameManager.getCurrentGameScene().connection?.userRoomToken;
    }

    const enabledOf = (kindFilter: MaterialFamily["kind"]) =>
        families.filter((family) => family.kind === kindFilter && family.enabled);

    const bandedRoofs = $derived(
        enabledOf("roof").filter((family) => !family.id.startsWith("roof:shingle-")),
    );
    const mansardRoofs = $derived(enabledOf("roof").filter((family) => family.id.startsWith("roof:shingle-")));
    const roofChoices = $derived(kind === "mansard" ? mansardRoofs : bandedRoofs);
    const wallChoices = $derived(enabledOf("wall"));
    const storefrontChoices = $derived(enabledOf("storefront"));

    // Bare id for the compiler record (strip the namespace prefix).
    const bare = (id: string) => id.replace(/^(roof|wall|storefront):/, "");

    onMount(async () => {
        try {
            const token = roomToken();
            const response = await fetch("/editor-assets/building-materials.json", {
                method: "GET",
                headers: token ? { "x-dr-room-token": token } : {},
                credentials: "omit",
                cache: "no-store",
                redirect: "error",
            });
            if (!response.ok) throw new Error(`materials ${response.status}`);
            const body = (await response.json()) as { families: MaterialFamily[] };
            families = body.families;
        } catch (error) {
            loadError = error instanceof Error ? error.message : String(error);
        }
    });

    function setFloorCount(count: number): void {
        floorCount = count;
        floorFamilies = Array.from({ length: count }, (_, i) => floorFamilies[i] ?? "");
    }

    function cancel(): void {
        pendingBuildingFootprintStore.set(null);
        status = null;
    }

    async function confirm(): Promise<void> {
        if (!footprint) return;
        status = null;
        submitting = true;
        try {
            const token = roomToken();
            const record = {
                kind,
                x: footprint.x,
                y: footprint.y,
                width: footprint.width,
                depth: footprint.depth,
                ...(kind === "banded" ? { roofRecipe: "pyramid-hip" } : {}),
                roofFamily: bare(roofFamily),
                floors: floorFamilies.filter(Boolean).map((family) => ({ family: bare(family) })),
                ...(storefront ? { storefront: bare(storefront) } : {}),
            };
            const response = await fetch("/editor-assets/building", {
                method: "POST",
                headers: {
                    "content-type": "application/json",
                    ...(token ? { "x-dr-room-token": token } : {}),
                },
                credentials: "omit",
                cache: "no-store",
                redirect: "error",
                body: JSON.stringify(record),
            });
            if (response.status === 201) {
                pendingBuildingFootprintStore.set(null);
                status = "Submitted — appears after the next map build.";
            } else {
                const detail = (await response.json().catch(() => ({}))) as { error?: string };
                status = `Rejected: ${detail.error ?? response.status}`;
            }
        } catch (error) {
            status = `Failed: ${error instanceof Error ? error.message : String(error)}`;
        } finally {
            submitting = false;
        }
    }
</script>

<div class="building-editor flex flex-col gap-3 text-white">
    <h3 class="text-lg font-bold">{$LL.mapEditor.sideBar.buildingEditor()}</h3>

    {#if loadError}
        <p class="text-red-400 text-sm">Could not load materials ({loadError}).</p>
    {:else if !footprint}
        <p class="text-sm opacity-80">Drag a rectangle on the map to set the building footprint.</p>
    {:else}
        <p class="text-sm opacity-80">
            Footprint: <b>{footprint.width}×{footprint.depth}</b> tiles at ({footprint.x}, {footprint.y}).
            The building rises above it.
        </p>

        <label class="flex flex-col gap-1 text-sm">
            <span class="opacity-80">Kind</span>
            <select bind:value={kind} class="text-black rounded px-2 py-1">
                <option value="banded">Banded (pyramid, width 4–5)</option>
                <option value="mansard">Mansard (width ≥ 7)</option>
            </select>
        </label>

        <label class="flex flex-col gap-1 text-sm">
            <span class="opacity-80">Roof</span>
            <select bind:value={roofFamily} class="text-black rounded px-2 py-1">
                <option value="">Select a roof…</option>
                {#each roofChoices as family (family.id)}
                    <option value={family.id}>{family.name}</option>
                {/each}
            </select>
        </label>

        <label class="flex flex-col gap-1 text-sm">
            <span class="opacity-80">Floors</span>
            <select
                value={floorCount}
                onchange={(event) => setFloorCount(Number(event.currentTarget.value))}
                class="text-black rounded px-2 py-1"
            >
                <option value={1}>1 floor</option>
                <option value={2}>2 floors</option>
                <option value={3}>3 floors</option>
            </select>
        </label>

        {#each floorFamilies as _floor, index (index)}
            <label class="flex flex-col gap-1 text-sm">
                <span class="opacity-80">Floor {index + 1} (bottom-first)</span>
                <select bind:value={floorFamilies[index]} class="text-black rounded px-2 py-1">
                    <option value="">Select a wall…</option>
                    {#each wallChoices as family (family.id)}
                        <option value={family.id}>{family.name}</option>
                    {/each}
                </select>
            </label>
        {/each}

        <label class="flex flex-col gap-1 text-sm">
            <span class="opacity-80">Storefront (optional)</span>
            <select bind:value={storefront} class="text-black rounded px-2 py-1">
                <option value="">None</option>
                {#each storefrontChoices as family (family.id)}
                    <option value={family.id}>{family.name}</option>
                {/each}
            </select>
        </label>

        <div class="flex gap-2 pt-1">
            <button
                class="bg-emerald-600 hover:bg-emerald-500 rounded px-3 py-1 disabled:opacity-50"
                disabled={submitting || !roofFamily || floorFamilies.filter(Boolean).length === 0}
                onclick={confirm}
            >
                Confirm
            </button>
            <button class="bg-gray-600 hover:bg-gray-500 rounded px-3 py-1" onclick={cancel}>
                Cancel
            </button>
        </div>
    {/if}

    {#if status}
        <p class="text-sm opacity-90">{status}</p>
    {/if}
</div>
